import networkService, {tenantAdminHeaders} from '../../Network/NetworkManager';
import AppConstants from '../../Constants/AppConstants';
import {getCookiesValue, getCurrentUser} from '../../HelperClasses/UserPrefrences';

const APPURL = require('../../Constants/URLConstants');

const errorMessage = (response, fallback) =>
  response?.error?.message || response?.message || fallback;
const REQUEST_TIMEOUT_MS = 12000;
const ATTRIBUTION_API_BASE = 'https://attribution.tradly.app/api';

// These admin endpoints can return 401 for a disabled feature or missing
// permission, so they must not trigger the global logout.
const ensureBearerToken = () => {
  if (AppConstants.BearerAuthorization) {
    return Promise.resolve(AppConstants.BearerAuthorization);
  }
  return new Promise(resolve => {
    getCurrentUser(token => {
      if (token) {
        AppConstants.BearerAuthorization = token;
        resolve(token);
        return;
      }

      const cookieToken =
        networkService.getAuthKey() ||
        getCookiesValue(`ck_${AppConstants.cookieEnvironment}_a`);
      if (cookieToken) AppConstants.BearerAuthorization = cookieToken;
      resolve(cookieToken || '');
    });
  });
};

const call = async (path, method, body, options = {}) => {
  // The auth shell restores the encrypted admin token asynchronously. Wait for
  // that restore so the shared NetworkManager can attach Authorization before
  // the request reaches the API gateway.
  await ensureBearerToken();
  const request = networkService.networkCall(
    path,
    method,
    body === undefined ? '' : JSON.stringify(body),
    '',
    '',
    '',
    '',
    '',
    {...options, skipLogout: true},
  );
  const timeout = new Promise((_, reject) => {
    setTimeout(() => reject(new Error('Request timed out.')), REQUEST_TIMEOUT_MS);
  });
  const response = await Promise.race([request, timeout]);
  if (response?.status === true || (response && response.status === undefined && !response.error)) {
    return response;
  }
  throw new Error(errorMessage(response, 'Request failed. Please try again.'));
};

const attributionCall = async path => {
  const token = await ensureBearerToken();
  const response = await fetch(`${ATTRIBUTION_API_BASE}${path}`, {
    headers: {
      Accept: 'application/json',
      ...(token ? {Authorization: `Bearer ${token}`} : {}),
    },
  });
  const text = await response.text();
  let body = null;
  try { body = text ? JSON.parse(text) : null; } catch (_) { body = null; }
  if (!response.ok) throw new Error(errorMessage(body, `Attribution request failed (${response.status})`));
  return body;
};

export const unwrapList = response => {
  // Some endpoints return the payload directly (for example
  // `{suppliers: [...]}`), while others wrap it in `data`. Support both
  // shapes so a successful GET is not rendered as an empty table.
  const data = response?.data ?? response;
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object') {
    const list = Object.values(data).find(Array.isArray);
    if (list) return list;
  }
  return [];
};

export const unwrapItem = response => {
  const data = response?.data ?? response;
  if (!data || typeof data !== 'object' || Array.isArray(data)) return data ?? null;
  const values = Object.values(data);
  if (values.length === 1 && values[0] && typeof values[0] === 'object') return values[0];
  return data;
};

export const adminApi = {
  list: async path => unwrapList(await call(path, 'GET')),
  get: async path => unwrapItem(await call(path, 'GET')),
  post: (path, body = {}, options = {}) => call(path, 'POST', body, options),
  put: (path, body = {}, options = {}) => call(path, 'PUT', body, options),
  remove: path => call(path, 'DELETE'),

  download: async (path, filename) => {
    const baseUrl = AppConstants.isDevelopment
      ? APPURL.URLPaths.BaseURL
      : getCookiesValue(AppConstants.tradlyEVN) === 'sandbox'
        ? APPURL.URLPaths.sandboxBaseURL
        : APPURL.URLPaths.productionBaseURL;
    const headers = {
      'x-agent': 1,
      'x-auth-key': networkService.getAuthKey(),
      ...tenantAdminHeaders(),
    };
    if (AppConstants.BearerAuthorization) {
      headers.Authorization = `Bearer ${AppConstants.BearerAuthorization}`;
    }
    const response = await fetch(`${baseUrl}${path}`, {headers});
    if (!response.ok) {
      const text = await response.text();
      let message = `Export failed (${response.status}).`;
      try {
        message = errorMessage(JSON.parse(text), message);
      } catch (_) {}
      throw new Error(message);
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  },
};

export const attributionApi = {
  get: attributionCall,
};
